import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { calculatorAPI } from '../../lib/api';
import { CalculatorForm } from './CalculatorForm';
import { LeadGate } from './LeadGate';
import { CalculatorResults } from './CalculatorResults';
import { onlyDigits } from './calcUtils';

const INITIAL_FORM = { revenue: '', payroll: '', regime: 'nao_sei', municipality: 'boa-vista-rr', partners: '1', service: '', profession: '' };
const INITIAL_LEAD = { name: '', phone: '', email: '' };

const STEP_TITLES = {
  1: { title: 'Calculadora de Impostos para Médicos', desc: 'Informe seus dados e compare a carga tributária em todos os regimes.' },
  2: { title: 'Quase lá!', desc: 'Identifique-se para visualizar sua comparação completa.' },
  3: { title: 'Seu resultado', desc: 'Comparação da carga tributária mensal entre os regimes.' },
};

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2 mb-2" data-testid="calc-step-indicator">
    {[1, 2, 3].map((s) => (
      <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'bg-[#007f73] flex-1' : 'bg-slate-200 flex-1'}`} />
    ))}
  </div>
);

const TaxCalculatorDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [lead, setLead] = useState(INITIAL_LEAD);
  const [municipalities, setMunicipalities] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && municipalities.length === 0) {
      calculatorAPI.getMunicipalities()
        .then((res) => setMunicipalities(res.data))
        .catch(() => toast.error('Erro ao carregar municípios'));
    }
  }, [open, municipalities.length]);

  const handleRestart = () => {
    setStep(1);
    setForm(INITIAL_FORM);
    setLead(INITIAL_LEAD);
    setResults(null);
  };

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) handleRestart();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await calculatorAPI.submit({
        name: lead.name.trim(),
        phone: lead.phone.trim(),
        email: lead.email.trim(),
        monthly_revenue: Number(form.revenue),
        monthly_payroll: Number(form.payroll || 0),
        current_regime: form.regime,
        municipality_id: form.municipality,
        num_partners: Math.max(1, Number(onlyDigits(String(form.partners)) || 1)),
        main_service: form.service,
        non_medical_profession: form.service === 'outra_nao_medica' ? form.profession : '',
      });
      setResults(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao calcular. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const { title, desc } = STEP_TITLES[step];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="tax-calculator-dialog">
        <DialogHeader>
          <StepIndicator step={step} />
          <DialogTitle className="font-heading text-xl md:text-2xl text-slate-900">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        {step === 1 && (
          <CalculatorForm form={form} setForm={setForm} municipalities={municipalities} onNext={() => setStep(2)} />
        )}
        {step === 2 && (
          <LeadGate lead={lead} setLead={setLead} loading={loading} onBack={() => setStep(1)} onSubmit={handleSubmit} />
        )}
        {step === 3 && results && (
          <CalculatorResults results={results} onRestart={handleRestart} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaxCalculatorDialog;
